import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { API_URLS } from '../config';
import { apiRequest } from '../utils/apiRequest';

const ACCENT_COLORS = ['#437cf0', '#e74c3c', '#b0d182', '#f39c12', '#8e44ad', '#16a085'];

const FolderIcon = ({ color }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="90" height="90" viewBox="0 0 64 64" fill="none">
    <rect x="4" y="18" width="56" height="38" rx="4" fill={color + '22'} stroke={color} strokeWidth="2.5" />
    <path d="M4 22c0-2.2 1.8-4 4-4h18l4 5H56c2.2 0 4 1.8 4 4v3H4V22z" fill={color + '44'} stroke={color} strokeWidth="2.5" />
    <rect x="10" y="30" width="26" height="3" rx="1.5" fill={color} opacity="0.5" />
    <rect x="10" y="36" width="18" height="3" rx="1.5" fill={color} opacity="0.3" />
  </svg>
);

const CloudIcon = ({ color }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="38" height="28" viewBox="0 0 38 28" fill="none">
    <ellipse cx="19" cy="18" rx="16" ry="8" fill={color + '22'} />
    <ellipse cx="12" cy="16" rx="10" ry="7" fill={color + '22'} />
    <ellipse cx="26" cy="15" rx="10" ry="7" fill={color + '22'} />
    <ellipse cx="19" cy="13" rx="13" ry="8" fill={color + '33'} />
  </svg>
);

const BookCard = ({ companyName, link, accentColor }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 4,
        boxShadow: hovered ? '0 8px 36px rgba(0,0,0,0.16)' : '0 4px 24px rgba(0,0,0,0.10)',
        transform: hovered ? 'translateY(-4px)' : 'translateY(0)',
        width: 300,
        padding: '36px 28px 28px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'relative',
        border: '1px solid #ccc',
        transition: 'box-shadow 0.2s, transform 0.2s',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="book-card"
    >
      {/* Cloud decoration */}
      <div style={{ position: 'absolute', top: 18, right: 22, opacity: 0.7 }}>
        <CloudIcon color={accentColor} />
      </div>

      {/* Folder icon */}
      <div style={{ marginBottom: 20, marginTop: 8 }}>
        <FolderIcon color={accentColor} />
      </div>

      {/* Divider */}
      <div style={{ width: '70%', height: 3, background: accentColor, borderRadius: 2, marginBottom: 20 }} />

      {/* Company name as title */}
      <h3 style={{ margin: '0 0 10px', fontSize: 18, fontWeight: 700, color: '#1a1a2e', textAlign: 'center', wordBreak: 'break-word' }}>
        {companyName}
      </h3>

      <p style={{ margin: '0 0 28px', fontSize: 13, color: '#666', textAlign: 'center', lineHeight: 1.6 }}>
        Access past inspection records and details for {companyName}.
      </p>

      {/* Button */}
      {link ? (
        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-block',
            background: accentColor,
            color: '#fff',
            fontWeight: 500,
            fontSize: 13,
            letterSpacing: 1,
            padding: '10px 30px',
            borderRadius: 6,
            textDecoration: 'none',
            textTransform: 'uppercase',
            opacity: hovered ? 0.88 : 1,
            transition: 'opacity 0.15s',
          }}
        >
          Open
        </a>
      ) : (
        <span style={{ fontSize: 12, color: '#aaa', fontStyle: 'italic' }}>No link configured</span>
      )}
    </div>
  );
};

export default function PastRecords() {
  const { user, token } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  const isGlobalAdmin = user?.role === 'global_superadmin';

  useEffect(() => {
    if (!token) return;
    const fetchCompanies = async () => {
      const url = isGlobalAdmin ? API_URLS.COMPANIES : `${API_URLS.COMPANIES}/my`;
      const res = await apiRequest(url, {}, token);
      if (res?.ok) {
        setCompanies(Array.isArray(res.data) ? res.data : [res.data]);
      }
      setLoading(false);
    };
    fetchCompanies();
  }, [token, isGlobalAdmin]);

  return (
    <div style={{
      minHeight: '80vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 20px',
      background: '#f4f6fb',
    }}>
      <h2 style={{ marginBottom: 8, color: '#1a2a4d', fontWeight: 700, fontSize: 26 }}>Past Records</h2>
      <p style={{ color: '#888', marginBottom: 48, fontSize: 14 }}>Select a company Work book to access its records</p>

      {loading ? (
        <p style={{ color: '#999' }}>Loading…</p>
      ) : companies.length === 0 ? (
        <p style={{ color: '#aaa', fontStyle: 'italic' }}>No records available for your account.</p>
      ) : (
        <div style={{ display: 'flex', gap: 40, flexWrap: 'wrap', justifyContent: 'center' }}>
          {companies.map((c, i) => (
            <BookCard
              key={c._id || c.id}
              companyName={c.companyName}
              link={c.pastRecordsLink || ''}
              accentColor={ACCENT_COLORS[i % ACCENT_COLORS.length]}
            />
          ))}
        </div>
      )}
    </div>
  );
}
